// src/tattoo/reactionsState.js
import { proxy } from "valtio";
import { supabase } from "./lib/supabaseClient";
import { getGuestId } from "./guestIdentity";

/**
 * Reactions model:
 *  table: reactions
 *  columns: id, layer_id, entity_id, user_id, guest_id, created_at
 *
 *  We treat the "reactor" as either user_id (logged-in) or guest_id (shared viewer).
 */

export const reactionsStore = proxy({
  // layerId -> { count: number, mine: boolean }
  byLayer: {},
});

/** Internal: set state for a layer */
function setLayerReaction(layerId, { count, mine }) {
  const prev = reactionsStore.byLayer[layerId] || { count: 0, mine: false };
  reactionsStore.byLayer = {
    ...reactionsStore.byLayer,
    [layerId]: {
      count: typeof count === "number" ? count : prev.count,
      mine: typeof mine === "boolean" ? mine : prev.mine,
    },
  };
}

// 👇 exported so realtime helper can update counts
export { setLayerReaction };

/** Public selector used by UI */
export function getVote(layerId) {
  return reactionsStore.byLayer[layerId] || { count: 0, mine: false };
}

/**
 * Export only the reaction *counts* so state.js can embed them
 * into the shared project payload.
 *
 * Shape: { [layerId]: number }
 */
export function exportCountsOnly() {
  const out = {};
  const src = reactionsStore.byLayer || {};

  for (const [layerId, v] of Object.entries(src)) {
    const n = typeof v?.count === "number" ? v.count : 0;
    if (n > 0) out[layerId] = n;
  }

  return out;
}

/**
 * Resolve a "reactor key":
 * - logged-in user → user.id
 * - guest → guest_id (stable per browser)
 */
async function getReactorInfo() {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (!error && data?.user) {
      return { mode: "user", id: data.user.id };
    }
  } catch {
    // ignore, fall through to guest
  }
  return { mode: "guest", id: getGuestId() };
}

/** Load counts for a set of layerIds */
export async function preloadReactionCounts(layerIds) {
  const ids = (layerIds || []).filter(Boolean);
  if (!ids.length) return;

  try {
    // Aggregate counts for these layers
    const { data, error } = await supabase
      .from("reactions")
      .select("layer_id")
      .in("layer_id", ids);

    if (error) {
      console.warn("[reactions] preload failed:", error);
      return;
    }

    const counts = {};
    for (const row of data || []) {
      counts[row.layer_id] = (counts[row.layer_id] || 0) + 1;
    }

    // Figure out which ones are "mine"
    const me = await getReactorInfo();
    let mineSet = new Set();

    if (me.mode === "user") {
      const { data: mineRows } = await supabase
        .from("reactions")
        .select("layer_id")
        .eq("user_id", me.id)
        .in("layer_id", ids);
      mineSet = new Set((mineRows || []).map((r) => r.layer_id));
    } else {
      const { data: mineRows } = await supabase
        .from("reactions")
        .select("layer_id")
        .eq("guest_id", me.id)
        .in("layer_id", ids);
      mineSet = new Set((mineRows || []).map((r) => r.layer_id));
    }

    for (const id of ids) {
      setLayerReaction(id, {
        count: counts[id] || 0,
        mine: mineSet.has(id),
      });
    }
  } catch (err) {
    console.warn("[reactions] preload error:", err);
  }
}

/** Toggle vote for a given layer (or project-global key) */
export async function toggleVote(layerId, _source = "local") {
  if (!layerId) return;
  const current = getVote(layerId);

  const me = await getReactorInfo();
  const reactorPayload =
    me.mode === "user"
      ? { user_id: me.id, guest_id: null }
      : { user_id: null, guest_id: me.id };

  if (current.mine) {
    // Remove my reaction
    try {
      let query = supabase
        .from("reactions")
        .delete()
        .eq("layer_id", layerId)
        .eq("entity_id", layerId); // keep rows tidy

      if (me.mode === "user") {
        query = query.eq("user_id", me.id);
      } else {
        query = query.eq("guest_id", me.id);
      }
      const { error } = await query;
      if (error) throw error;

      setLayerReaction(layerId, {
        count: Math.max(0, (current.count || 1) - 1),
        mine: false,
      });
    } catch (err) {
      console.warn("[reactions] remove failed:", err);
    }
  } else {
    // Add my reaction
    try {
      const payload = {
        layer_id: layerId,
        entity_id: layerId, // 👈 satisfies NOT NULL constraint
        ...reactorPayload,
      };

      const { data, error } = await supabase
        .from("reactions")
        .insert([payload])
        .select()
        .single();

      if (error) {
        // Ignore duplicate-key errors; they just mean we double-clicked
        if (String(error.code) !== "23505") {
          console.warn("[reactions] insert failed:", error);
        }
      } else if (data) {
        setLayerReaction(layerId, {
          count: (current.count || 0) + 1,
          mine: true,
        });
      }
    } catch (err) {
      console.warn("[reactions] insert error:", err);
    }
  }
}

/**
 * Apply reaction counts from a shared project payload.
 * Shape of `counts`: { [layerId]: number }
 * Used in state.js when loading a /view project hash.
 */
export function applyProjectReactions(counts) {
  if (!counts || typeof counts !== "object") return;

  const next = { ...(reactionsStore.byLayer || {}) };

  for (const [layerId, total] of Object.entries(counts)) {
    if (typeof total !== "number") continue;

    const prev = next[layerId] || { count: 0, mine: false };
    next[layerId] = { ...prev, count: total };
  }

  reactionsStore.byLayer = next;
}